import type { DomainEvent } from "../../models/events.ts";
import { areAdjacent } from "../../models/hex/hexGrid.ts";
import type { TileKey, WorldState } from "../../models/worldState.ts";
import { applyRandomEventTileEntry } from "../events/applyRandomEventTileEntry.ts";
import { revealTile } from "../map/tileVisibility.ts";
import { rejectCommand } from "../rejectCommand.ts";
import type { ReduceResult } from "../result.ts";

export function reduceMove(state: WorldState, tile: TileKey): ReduceResult {
  const command = { type: "Move" as const, tile };
  const destination = state.tiles[tile];

  if (state.clan.movedThisTurn) {
    return rejectCommand(state, command, "already-moved");
  }

  if (!destination) {
    return rejectCommand(state, command, "no-such-tile");
  }

  const { q, r } = state.clan.currentTile;
  if (destination.q === q && destination.r === r) {
    return rejectCommand(state, command, "same-tile");
  }

  if (!areAdjacent(state.clan.currentTile, destination)) {
    return rejectCommand(state, command, "not-adjacent");
  }

  const wasHidden = destination.visibility === "hidden";
  const nextClanTile = { q: destination.q, r: destination.r };
  const midState: WorldState = {
    ...state,
    tiles: {
      ...state.tiles,
      [tile]: revealTile(destination),
    },
    clan: {
      ...state.clan,
      currentTile: nextClanTile,
      movedThisTurn: true,
    },
  };

  const randomEventResult = applyRandomEventTileEntry(midState, nextClanTile);
  const events: DomainEvent[] = [
    { type: "TerrainEntered", terrain: destination.terrain },
    ...randomEventResult.events,
  ];

  if (wasHidden) {
    const revealedTileCount = Object.values(midState.tiles).filter(
      (entry) => entry.visibility === "revealed",
    ).length;
    events.push({
      type: "TileRevealed",
      revealedTileCount,
      turn: midState.turn,
    });
  }

  return {
    state: {
      ...midState,
      randomEvent: randomEventResult.randomEvent,
    },
    events,
  };
}
